import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useUser } from "../../../../hooks/use-user";
import axios from "../../../../axios";
import { toast } from "react-toastify";
import {
  CheckCircle2,
  AlertCircle,
  Gem,
  Zap,
  Star,
  ShieldCheck,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";

function LearnPlan() {
  const { data: user } = useUser();
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCheckOutPage, setShowCheckOutPage] = useState(true);
  const [gatewayVisibilityWeb, setgatewayVisibilityWeb] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setPageLoading(true);
      try {
        const response = await axios.get("getAllCourses");
        if (response?.data) {
          setData(response?.data?.result);
          localStorage.setItem("plans", JSON.stringify(response?.data?.result));
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setPageLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function gatewayVisibility() {
      try {
        const response = await axios.get("getGetwaySetting");
        if (
          Array.isArray(response.data.result) &&
          response.data.result.length > 0
        ) {
          const latest = response.data.result[response.data.result.length - 1];
          setShowCheckOutPage(latest.showcheckOutPageInWeb);
          setgatewayVisibilityWeb(latest.gatewayVisibilityWeb);
        }
      } catch (error) {
        console.error("Error fetching gateway settings:", error);
      }
    }
    gatewayVisibility();
  }, []);

  const handlePayment = (courseId) => {
    setLoading(true);
    axios
      .post("createOrder", { courseId })
      .then((response) => {
        if (response?.data) {
          window.location.href = response.data?.result?.upiLink;
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error("Payment initiation failed");
      });
  };

  const handlePayNow = (courseId) => {
    if (!gatewayVisibilityWeb) {
      alert("Due to some technical issue payment is not being processed");
      return;
    }
    handlePayment(courseId);
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#2A1B3D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2A1B3D] pt-20 pb-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none"></div>
      <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-[-100px] w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 text-purple-300 text-sm font-medium mb-6 border border-purple-500/20">
              <Gem size={16} className="text-purple-400" />
              Premium Learning Experience
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Choose the Perfect Plan for <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Your Success
              </span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              Unlock unlimited access to premium courses, expert mentorship, and
              real-world projects. Start your journey today.
            </p>
          </motion.div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {data.map((plan, index) => {
            const isTopSelling = index === 0; // Assuming first plan is top selling for now, currently dummy logic in original code
            const isActive = user?.plan === plan._id;

            return (
              <motion.div
                key={plan._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative flex flex-col bg-[#3F234E] rounded-3xl p-1 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-purple-500/20
                  ${isActive ? "ring-2 ring-green-500" : ""}
                `}
              >
                {/* Top Selling Badge */}
                {isTopSelling && !isActive && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Star size={12} fill="currentColor" />
                      MOST POPULAR
                    </span>
                  </div>
                )}

                {isActive && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <span className="bg-green-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <CheckCircle2 size={12} />
                      CURRENT PLAN
                    </span>
                  </div>
                )}

                <div className="flex-1 bg-[#2A1B3D]/50 rounded-[22px] p-6 flex flex-col h-full border border-white/5 group-hover:border-purple-500/30 transition-colors">
                  {/* Plan Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center border border-white/10 group-hover:border-purple-500/30 transition-colors">
                      <img
                        src={plan.planImage}
                        alt={plan.planName}
                        className="w-8 h-8 object-contain drop-shadow-md"
                      />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg leading-tight group-hover:text-purple-300 transition-colors">
                        {plan.planName}
                      </h3>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-2xl font-bold text-white">
                          ₹{plan.offerPrice}
                        </span>
                        {plan.price > plan.offerPrice && (
                          <span className="text-sm text-gray-500 line-through decoration-white/30">
                            ₹{plan.price}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Features Divider */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6"></div>

                  {/* Features List */}
                  <div className="flex-1 mb-8">
                    <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                      <ShieldCheck size={14} /> What you get
                    </h4>
                    <ul className="space-y-3">
                      {plan.exams.map((exam, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 text-sm text-gray-300"
                        >
                          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="leading-snug">{exam.examName}</span>
                        </li>
                      ))}
                      {/* Placeholder features if list is short to make cards even */}
                      {plan.exams.length < 3 && (
                        <>
                          <li className="flex items-start gap-3 text-sm text-gray-300">
                            <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <span className="leading-snug">
                              Full Analytics Access
                            </span>
                          </li>
                          <li className="flex items-start gap-3 text-sm text-gray-300">
                            <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <span className="leading-snug">24/7 Support</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <div className="mt-auto">
                    {isActive ? (
                      <Link to="/dash" className="block w-full">
                        <button className="w-full py-3.5 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-sm hover:bg-white/10 transition-all flex items-center justify-center gap-2 group-active">
                          Go to Dashboard
                          <ArrowRight
                            size={16}
                            className="group-active-hover:translate-x-1 transition-transform"
                          />
                        </button>
                      </Link>
                    ) : (
                      <>
                        {showCheckOutPage ? (
                          <Link
                            to={`/dash/checkout/${plan._id}`}
                            className="block w-full"
                          >
                            <button className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2">
                              Get Started Now
                              <ChevronRight size={16} />
                            </button>
                          </Link>
                        ) : (
                          <button
                            onClick={() => handlePayNow(plan._id)}
                            disabled={loading || !gatewayVisibilityWeb}
                            className={`w-full py-3.5 rounded-xl font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition-all
                              ${
                                loading || !gatewayVisibilityWeb
                                  ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                                  : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98]"
                              }`}
                          >
                            {loading ? (
                              <>
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                Buy Now <Zap size={16} className="fill-white" />
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <AlertCircle size={14} />
            Need help choosing?{" "}
            <a
              href="/contact"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LearnPlan;
